import { useState } from 'react';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Unstable_Grid2';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';

import { DashboardContent } from 'src/layouts/dashboard';

import { Chart, useChart } from 'src/components/chart';
import { Iconify } from 'src/components/iconify';
import { ReportScopeFilter } from 'src/components/report-filter/ReportScopeFilter';
import { useReportScope } from 'src/routes/context/ReportScopeContext';

import { downloadBlob } from 'src/utils/download-file';

import { severity, SCORE_FORMULA_TR } from 'src/sections/reports/utils/score-severity';

import {
  getEmployeeReport,
  exportEmployeeReport,
  type SignalType,
  type ExportFormat,
  type SingleEmployeeReport,
} from 'src/api/reports.api';

const SIGNAL_LABEL: Record<SignalType, { label: string; color: 'warning' | 'info' | 'error' | 'default' }> = {
  LATE: { label: 'Geç giriş', color: 'warning' },
  EARLY_LEAVE: { label: 'Erken çıkış', color: 'info' },
  ABSENCE: { label: 'Devamsızlık', color: 'error' },
  FORGOTTEN_CHECKOUT: { label: 'Unutulan çıkış', color: 'default' },
};

function isoDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function Kpi({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <Card sx={{ p: 2, textAlign: 'center' }}>
      <Typography variant="h4" sx={{ color: color ?? 'text.primary' }}>{value}</Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>{label}</Typography>
    </Card>
  );
}

function SingleEmployeeInner() {
  const { employeeId } = useReportScope();
  const [start, setStart] = useState(isoDaysAgo(30));
  const [end, setEnd] = useState(isoDaysAgo(0));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<SingleEmployeeReport | null>(null);
  const [exporting, setExporting] = useState<ExportFormat | null>(null);

  const handleFetch = async () => {
    if (!employeeId) {
      setError('Lütfen bir çalışan seçin.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      setData(await getEmployeeReport(employeeId, start, end));
    } catch (e) {
      setError('Rapor yüklenemedi. Yetki veya bağlantı sorununu kontrol edin.');
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: ExportFormat) => {
    if (!employeeId) return;
    setExporting(format);
    try {
      const blob = await exportEmployeeReport(employeeId, start, end, format);
      downloadBlob(blob, `personel-rapor-${start}_${end}`, format);
    } catch (e) {
      setError('Dışa aktarma başarısız oldu.');
    } finally {
      setExporting(null);
    }
  };

  const chartOptions = useChart({
    xaxis: { categories: data?.workHours.map((p) => p.date.slice(5)) ?? [] },
    tooltip: { y: { formatter: (v: number) => `${v} saat` } },
  });

  const reg = data?.regularity;

  return (
    <DashboardContent>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4">Personel Raporu</Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
          Tek bir çalışanın gün-gün devam dökümü ve günlük çalışma saatleri.
        </Typography>
        <Box
          sx={{
            mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: 'background.neutral',
            display: 'inline-flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center',
          }}
        >
          <Iconify icon="solar:info-circle-bold" width={18} sx={{ color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>{SCORE_FORMULA_TR}</Typography>
        </Box>
      </Box>

      <Card sx={{ p: 3, mb: 3 }}>
        <ReportScopeFilter showEmployee requireBranch />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            size="small" type="date" label="Başlangıç" InputLabelProps={{ shrink: true }}
            value={start} onChange={(e) => setStart(e.target.value)}
          />
          <TextField
            size="small" type="date" label="Bitiş" InputLabelProps={{ shrink: true }}
            value={end} onChange={(e) => setEnd(e.target.value)}
          />
          <Button variant="contained" onClick={handleFetch} disabled={loading}>
            {loading ? 'Yükleniyor…' : 'Raporu Getir'}
          </Button>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined" color="error" startIcon={<Iconify icon="solar:file-text-bold" />}
            disabled={!data || exporting !== null} onClick={() => handleExport('PDF')}
          >
            {exporting === 'PDF' ? 'PDF…' : 'PDF'}
          </Button>
          <Button
            variant="outlined" color="success" startIcon={<Iconify icon="solar:file-download-bold" />}
            disabled={!data || exporting !== null} onClick={() => handleExport('EXCEL')}
          >
            {exporting === 'EXCEL' ? 'Excel…' : 'Excel'}
          </Button>
        </Stack>
        {error && <Typography variant="body2" sx={{ color: 'error.main', mt: 2 }}>{error}</Typography>}
      </Card>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {reg && !loading && (
        <Stack spacing={3}>
          <Box>
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
              <Typography variant="h6">
                {reg.fullName} — {reg.branchName}
              </Typography>
              {(() => {
                const sev = severity(reg.score, reg.expectedWorkdays);
                return <Chip size="small" color={sev.color} label={`Risk: ${sev.label} (%${sev.pct})`} />;
              })()}
            </Stack>
            <Grid container spacing={2}>
              <Grid xs={6} md={2}><Kpi label="Geç Giriş" value={reg.lateCount} color="warning.main" /></Grid>
              <Grid xs={6} md={2}><Kpi label="Erken Çıkış" value={reg.earlyLeaveCount} color="info.main" /></Grid>
              <Grid xs={6} md={2}><Kpi label="Devamsızlık" value={reg.absenceCount} color="error.main" /></Grid>
              <Grid xs={6} md={2}><Kpi label="Unutulan Çıkış" value={reg.forgottenCheckoutCount} /></Grid>
              <Grid xs={6} md={2}><Kpi label="İş Günü" value={reg.expectedWorkdays} /></Grid>
              <Grid xs={6} md={2}><Kpi label="Skor" value={reg.score} color="primary.main" /></Grid>
            </Grid>
          </Box>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>Günlük Çalışma Saatleri</Typography>
              <Chart
                type="line"
                series={[{ name: 'Saat', data: data!.workHours.map((p) => p.hours) }]}
                options={chartOptions}
                height={300}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>Gün-gün İhlal Dökümü</Typography>
              {reg.events.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Bu dönemde ihlal kaydı yok.
                </Typography>
              ) : (
                <Stack divider={<Divider flexItem />} spacing={1}>
                  {reg.events.map((ev, i) => {
                    const meta = SIGNAL_LABEL[ev.type];
                    return (
                      <Stack key={`${ev.date}-${i}`} direction="row" spacing={2} alignItems="center" sx={{ py: 0.5 }}>
                        <Typography variant="body2" sx={{ minWidth: 100, fontWeight: 500 }}>{ev.date}</Typography>
                        <Chip size="small" color={meta.color} label={meta.label} />
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                          {ev.checkIn ? `Giriş ${ev.checkIn.slice(0, 5)}` : ''}
                          {ev.checkOut ? ` · Çıkış ${ev.checkOut.slice(0, 5)}` : ''}
                          {ev.deviationMinutes != null ? ` · ${ev.deviationMinutes} dk sapma` : ''}
                        </Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}
    </DashboardContent>
  );
}

export function SingleEmployeeView() {
  return <SingleEmployeeInner />;
}
